/**
 * Generic TTL-кэш с дедупликацией concurrent fetch'ей.
 *
 * Решает Issue #3 из eng review: если несколько компонентов одновременно
 * делают mount и вызывают `ensureLoaded(key)`, fetch выполняется ровно один раз —
 * остальные вызовы ждут тот же Promise.
 *
 * Применяется в:
 *   - OrgWorkflowContext (per-orgId config)
 *   - TODO #1: audit DictsContext / AppFeaturesContext / ServiceTypesContext /
 *     PaymentScheduleDictsContext на тот же bug
 *
 * Без React-зависимостей — чистый TS. Компонент подписывается через optional
 * `onChange` callback (для re-render через useReducer/setState в consumer'е).
 */

export interface DedupedCacheEntry<T> {
  data: T | null;
  loadedAt: number;
  inFlight: Promise<T> | null;
  error: Error | null;
}

export interface DedupedCache<K, T> {
  /** Возвращает cached data или `null` если ещё не загружено/expired. */
  get(key: K): T | null;
  /** Lazy-load: возвращает кэш или выполняет fetch. Dedup'ирует concurrent вызовы. */
  ensureLoaded(key: K): Promise<T>;
  /** Принудительный re-fetch — стирает cache для key и дёргает fetcher. */
  refetch(key: K): Promise<T>;
  /** Стереть кэш для key (следующий `ensureLoaded` выполнит fetch). */
  invalidate(key: K): void;
  /** Стереть весь кэш. */
  invalidateAll(): void;
  /** `true`, если data для key сейчас загружается. */
  isLoading(key: K): boolean;
  /** Последняя ошибка для key, если была. */
  errorFor(key: K): Error | null;
  /** Подписаться на изменения кэша (вызывается после любого put/invalidate). */
  subscribe(listener: (key: K) => void): () => void;
}

export interface DedupedCacheOptions<K, T> {
  fetcher: (key: K) => Promise<T>;
  /** TTL в миллисекундах; после истечения `ensureLoaded` делает re-fetch. По умолчанию — infinity. */
  ttlMs?: number;
  /** Превращает key в строку для Map (по умолчанию `String(key)`). */
  hashKey?: (key: K) => string;
}

export function createDedupedCache<K, T>(options: DedupedCacheOptions<K, T>): DedupedCache<K, T> {
  const { fetcher, ttlMs = Number.POSITIVE_INFINITY, hashKey = (k: K) => String(k) } = options;
  const cache = new Map<string, DedupedCacheEntry<T>>();
  const listeners = new Set<(key: K) => void>();

  const notify = (key: K) => {
    for (const l of listeners) {
      try {
        l(key);
      } catch {
        // listener errors swallowed — cache должен продолжать работать
      }
    }
  };

  const isFresh = (entry: DedupedCacheEntry<T> | undefined): entry is DedupedCacheEntry<T> => {
    if (!entry || entry.data === null) return false;
    return Date.now() - entry.loadedAt < ttlMs;
  };

  const ensureEntry = (key: K): DedupedCacheEntry<T> => {
    const h = hashKey(key);
    let entry = cache.get(h);
    if (!entry) {
      entry = { data: null, loadedAt: 0, inFlight: null, error: null };
      cache.set(h, entry);
    }
    return entry;
  };

  const doFetch = (key: K, entry: DedupedCacheEntry<T>): Promise<T> => {
    const promise = fetcher(key)
      .then((data) => {
        entry.data = data;
        entry.loadedAt = Date.now();
        entry.error = null;
        entry.inFlight = null;
        notify(key);
        return data;
      })
      .catch((err) => {
        entry.error = err instanceof Error ? err : new Error(String(err));
        entry.inFlight = null;
        notify(key);
        throw entry.error;
      });
    entry.inFlight = promise;
    return promise;
  };

  return {
    get(key) {
      const entry = cache.get(hashKey(key));
      return isFresh(entry) ? (entry?.data ?? null) : null;
    },

    ensureLoaded(key) {
      const entry = ensureEntry(key);
      if (entry.inFlight) return entry.inFlight;
      if (isFresh(entry) && entry.data !== null) return Promise.resolve(entry.data);
      return doFetch(key, entry);
    },

    refetch(key) {
      const entry = ensureEntry(key);
      // Даже если есть in-flight — перезаписываем его новым (бывший будет проигнорирован в цепочке)
      return doFetch(key, entry);
    },

    invalidate(key) {
      const h = hashKey(key);
      const entry = cache.get(h);
      if (entry) {
        entry.data = null;
        entry.loadedAt = 0;
        // inFlight НЕ трогаем — он дорешит и результат попадёт в entry,
        // но следующий ensureLoaded проверит isFresh === false и сделает свежий fetch.
        notify(key);
      }
    },

    invalidateAll() {
      for (const entry of cache.values()) {
        entry.data = null;
        entry.loadedAt = 0;
      }
      for (const l of listeners) {
        try {
          // notify с фиктивным key: вызывающий перезагрузит всё что надо
          (l as (k: K | null) => void)(null);
        } catch {
          /* swallow */
        }
      }
    },

    isLoading(key) {
      return Boolean(cache.get(hashKey(key))?.inFlight);
    },

    errorFor(key) {
      return cache.get(hashKey(key))?.error ?? null;
    },

    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}
